import { motion } from 'framer-motion';
import { Download, Eye, FileText } from 'lucide-react';

export default function CatalogPage() {
  return (
    <main data-testid="catalog-page" className="min-h-screen bg-brand-cream">
      {/* Hero */}
      <section className="relative py-16 md:py-28 overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-brand-blue/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-brand-pink/20 rounded-full blur-3xl" />

        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-2xl mx-auto"
          >
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-dark/40">
              E-Katalog
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-brand-dark tracking-tight mt-3 mb-6">
              Ürün Kataloğumuz
            </h1>
            <p className="text-brand-dark/60 text-base md:text-lg leading-relaxed">
              Tüm ürünlerimiz ile ilgili detaylı bilgi almak için e-kataloğumuzu 
              görüntüleyebilir veya indirebilirsiniz.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Catalog Cards */}
      <section className="pb-16 md:pb-24">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Main Catalog */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="p-8 rounded-2xl bg-white border border-slate-100 hover:shadow-lg transition-shadow duration-300"
            >
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-pink to-brand-blue overflow-hidden flex items-center justify-center mb-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_ayz-modern-refresh/artifacts/v7g2xftl_bf9b2368-ab92-47b2-b299-567b89d2d406.jpeg"
                  alt="AyzPlastik"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-xl font-bold text-brand-dark mb-2">
                2025 Ürün Kataloğu
              </h3>
              <p className="text-brand-dark/50 text-sm mb-6 leading-relaxed">
                Tüm ürün yelpazamızı içeren kapsamlı kataloğumuz.
                Yeni ürünler ve güncel fiyat bilgileri dahil.
              </p>
              <div className="flex gap-3">
                <button
                  data-testid="catalog-main-view"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-dark text-white text-sm font-medium hover:bg-brand-dark/90 transition-colors duration-200"
                >
                  <Eye size={16} />
                  Görüntüle
                </button>
                <button
                  data-testid="catalog-main-download"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-brand-dark text-brand-dark text-sm font-medium hover:bg-brand-dark hover:text-white transition-colors duration-200"
                >
                  <Download size={16} />
                  İndir
                </button>
              </div>
            </motion.div>

            {/* New Products Catalog */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="p-8 rounded-2xl bg-brand-dark text-white hover:shadow-lg transition-shadow duration-300"
            >
              <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mb-6">
                <FileText size={24} className="text-brand-pink" />
              </div>
              <h3 className="text-xl font-bold mb-2">
                Yeni Ürünler Kataloğu
              </h3>
              <p className="text-white/50 text-sm mb-6 leading-relaxed">
                En son eklenen ürünlerimizi ve yeniliklerimizi içeren
                özel katalog.
              </p>
              <div className="flex gap-3">
                <button
                  data-testid="catalog-new-view"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-brand-dark text-sm font-medium hover:bg-white/90 transition-colors duration-200"
                >
                  <Eye size={16} />
                  Görüntüle
                </button>
                <button
                  data-testid="catalog-new-download"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-white/30 text-white text-sm font-medium hover:bg-white/10 transition-colors duration-200"
                >
                  <Download size={16} />
                  İndir
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Info */}
      <section className="pb-16 md:pb-24">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center p-8 rounded-2xl bg-gradient-to-r from-brand-pink/20 to-brand-blue/20"
          >
            <p className="text-brand-dark/60 text-sm">
              Kataloglarımız hakkında detaylı bilgi almak veya toplu sipariş vermek için 
              bizimle iletişime geçebilirsiniz.
            </p>
            <a
              href="https://wa.me/905542345454"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="catalog-whatsapp-btn"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-green-500 text-white text-sm font-medium hover:bg-green-600 transition-colors duration-200 mt-4"
            >
              WhatsApp ile İletişime Geç
            </a>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
